import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type RecognitionCtor =
  | (new () => SpeechRecognition)
  | (new () => any)
  | null;

export type STTOptions = {
  lang?: string
  interimResults?: boolean
  continuous?: boolean
  maxAlternatives?: number
  enableKeyboardShortcut?: boolean
};

export type STTCallbacks = {
  onFinal?: (text: string) => void
  onInterim?: (text: string) => void
  onStart?: () => void
  onEnd?: () => void
  onError?: (err: string) => void
};

export function useBrowserSTT(
  options: STTOptions = {},
  callbacks: STTCallbacks = {},
) {
  const {
    lang = typeof navigator !== 'undefined' ? navigator.language : 'de-DE',
    interimResults = true,
    continuous = false,
    maxAlternatives = 1,
    enableKeyboardShortcut = false,
  } = options

  const { onFinal, onInterim, onStart, onEnd, onError } = callbacks

  // Use refs to avoid recreating recognition on every callback change
  const onFinalRef = useRef(onFinal)
  const onInterimRef = useRef(onInterim)
  const onStartRef = useRef(onStart)
  const onEndRef = useRef(onEnd)
  const onErrorRef = useRef(onError)

  // Keep refs up to date
  useEffect(() => {
    onFinalRef.current = onFinal
    onInterimRef.current = onInterim
    onStartRef.current = onStart
    onEndRef.current = onEnd
    onErrorRef.current = onError
  }, [onFinal, onInterim, onStart, onEnd, onError])

  const getCtor = useCallback((): RecognitionCtor => {
    if (typeof window === 'undefined') return null
    const w = window as any
    return w.SpeechRecognition || w.webkitSpeechRecognition || null
  }, [])

  const supported = useMemo(() => !!getCtor(), [getCtor])

  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [currentLang, setCurrentLang] = useState(lang)

  // necessary to reinitialize recognition when ctor or language changes
  useEffect(() => {
    if (!supported) return

    const Ctor: any = getCtor()
    const rec: SpeechRecognition = new Ctor()

    rec.lang = currentLang
    rec.interimResults = interimResults
    rec.continuous = continuous
    rec.maxAlternatives = maxAlternatives

    rec.onstart = () => {
      setListening(true)
      onStartRef.current?.()
    }

    rec.onend = () => {
      setListening(false)
      setInterim('')
      onEndRef.current?.()
    }

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let finalText = ''
      let interimText = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i]
        const chunk = res[0]?.transcript ?? ''
        if (res.isFinal) finalText += chunk
        else interimText += chunk
      }

      if (interimResults) {
        setInterim(interimText)
        onInterimRef.current?.(interimText)
      }

      if (finalText.trim()) {
        onFinalRef.current?.(finalText.trim())
        setInterim('')
      }
    }

    rec.onerror = (e: any) => {
      const code = e?.error || 'unknown'
      onErrorRef.current?.(String(code))
      try { rec.stop() } catch {}
    }

    recognitionRef.current = rec

    return () => {
      try { recognitionRef.current?.stop() } catch {}
      recognitionRef.current = null
    }
  }, [
    supported,
    getCtor,
    currentLang,
    interimResults,
    continuous,
    maxAlternatives,
  ])

  const start = useCallback(() => {
    if (!supported) {
      onErrorRef.current?.('not-supported')
      return
    }
    const rec = recognitionRef.current
    if (!rec || listening) return
    setInterim('')
    try {
      // IMPORTANT: source needs to be user gesture
      rec.start()
    } catch {
      // swallow error
    }
  }, [supported])

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop()
    } catch {
      // swallow error
    }
  }, [])

  const toggle = useCallback(() => {
    if (listening) stop()
    else start()
  }, [listening, start, stop])

  // Keyboard shortcut: Ctrl+Shift+M to toggle recording
  useEffect(() => {
    if (!enableKeyboardShortcut || !supported) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault()
        toggle()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enableKeyboardShortcut, toggle, supported])

  return {
    supported,
    listening,
    interim,
    start,
    stop,
    toggle,
    lang: currentLang,
    setLang: setCurrentLang,
  }
}
